import type React from 'react';

import { Checkbox, Select, SelectOption } from '../../components/ui';
import { RankerFilters, SOURCE_LABELS, STRATEGIC_LABELS, SortMode } from './publicScoring';
import { Theme } from './api';

interface PublicRankerFiltersProps {
  filters: RankerFilters;
  themes: Theme[];
  sources: string[];
  visibleCount: number;
  totalCount: number;
  onChange: (filters: RankerFilters) => void;
}

const SORT_OPTIONS: Array<{ value: SortMode; label: string }> = [
  { value: 'priority-desc', label: 'Priority score' },
  { value: 'gain-desc', label: 'Gain high to low' },
  { value: 'pain-asc', label: 'Pain low to high' },
  { value: 'pain-desc', label: 'Pain high to low' },
  { value: 'theme', label: 'Theme' },
];

export default function PublicRankerFilters({
  filters,
  themes,
  sources,
  visibleCount,
  totalCount,
  onChange,
}: PublicRankerFiltersProps) {
  const patch = (next: Partial<RankerFilters>) => onChange({ ...filters, ...next });

  return (
    <section className="border-y border-gray-200 bg-white/95 px-4 py-4 dark:border-dark-border dark:bg-dark-surface/95">
      <div className="mx-auto flex max-w-7xl flex-col gap-4">
        <div className="grid gap-3 md:grid-cols-4">
          <Control label="Source">
            <Select value={filters.source} onValueChange={(value) => patch({ source: value })}>
              <SelectOption value="all">All sources</SelectOption>
              {sources.map((source) => (
                <SelectOption key={source} value={source}>
                  {SOURCE_LABELS[source] ?? source}
                </SelectOption>
              ))}
            </Select>
          </Control>
          <Control label="Strategic objective">
            <Select value={filters.strategic} onValueChange={(value) => patch({ strategic: value })}>
              <SelectOption value="all">All objectives</SelectOption>
              {Object.entries(STRATEGIC_LABELS).map(([value, label]) => (
                <SelectOption key={value} value={value}>
                  {label}
                </SelectOption>
              ))}
            </Select>
          </Control>
          <Control label="Theme">
            <Select value={filters.theme} onValueChange={(value) => patch({ theme: value })}>
              <SelectOption value="all">All themes</SelectOption>
              {themes.map((theme) => (
                <SelectOption key={String(theme.id)} value={String(theme.id)}>
                  {theme.name}
                </SelectOption>
              ))}
            </Select>
          </Control>
          <Control label="Sort by">
            <Select value={filters.sortBy} onValueChange={(value) => patch({ sortBy: value as SortMode })}>
              {SORT_OPTIONS.map((option) => (
                <SelectOption key={option.value} value={option.value}>
                  {option.label}
                </SelectOption>
              ))}
            </Select>
          </Control>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3 text-small">
          <Checkbox
            checked={filters.hideInactive}
            onChange={(event) => patch({ hideInactive: event.target.checked })}
            label="Hide shipped/in-flight"
          />
          <Checkbox
            checked={filters.top10Only}
            onChange={(event) => patch({ top10Only: event.target.checked })}
            label="Top 10 only"
          />
          <Checkbox
            checked={filters.showDeferred}
            onChange={(event) => patch({ showDeferred: event.target.checked })}
            label="Show deferred"
          />
          <span className="ml-auto text-gray-500 dark:text-gray-400">
            Showing {visibleCount} of {totalCount}
          </span>
        </div>
      </div>
    </section>
  );
}

function Control({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="space-y-1.5">
      <span className="text-micro font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</span>
      {children}
    </label>
  );
}
